/**
 * `Book.attributes` — типизированная версионируемая схема настроек книги.
 * Реализует 01-SPEC.md §3.8.
 *
 * Инварианты:
 * - неизвестные ключи при чтении **сохраняются**, но игнорируются (`.passthrough()`);
 * - у каждого поля есть значение по умолчанию, поэтому `parseBookAttributes({})`
 *   возвращает полный набор атрибутов;
 * - размер картинки здесь не задаётся — он выводится из раскладки (§3.10.3).
 */
import { z } from 'zod';
import { mmToPx, roundTo } from './units.js';
import { IMAGE_RESOLUTION_TIERS, SEEDREAM_V5_PRO_EDIT_MODEL_ID, WAN_3_0_IMAGE_TO_VIDEO_MODEL_ID, currentModelId, } from './model-params.js';
/** Id модели: дефолт для новых книг, подмена снятой с эксплуатации — для старых. */
const modelIdField = (fallback) => z.string().min(1).default(fallback).transform(currentModelId);
/** Текущая версия схемы `Book.attributes` (01-SPEC.md §3.8). */
export const BOOK_SCHEMA_VERSION = 1;
/**
 * Язык текста книги не зависит от региона и локали интерфейса (01-SPEC.md §3.8).
 *
 * Every language the pipeline can write a book in. Which of them a deployment offers is
 * brand data, `BrandConfig.bookLanguages`.
 */
export const SUPPORTED_BOOK_LANGUAGES = [
    'ru',
    'en',
    'uz',
    'ko',
    'ka',
    'tr',
    'tt',
    'fr',
    'es',
];
export const BookLanguageSchema = z.enum(SUPPORTED_BOOK_LANGUAGES);
/**
 * Every print size the renderer knows. Which of them a deployment offers, and what it calls
 * them, is brand data, `BrandConfig.printSizes`: a market's paper is the market's.
 */
export const PAGE_FORMATS = [
    'A5',
    'A4',
    'SQUARE_200',
    'LETTER',
    'PORTRAIT_8X10IN',
    'SQUARE_8_5IN',
    'CUSTOM',
];
export const PageFormatSchema = z.enum(PAGE_FORMATS);
/** Portrait trim size of each standard format, mm. Inch sizes are exact: 1 in = 25.4 mm. */
export const PAGE_FORMAT_SIZES_MM = {
    A5: { widthMm: 148, heightMm: 210 },
    A4: { widthMm: 210, heightMm: 297 },
    SQUARE_200: { widthMm: 200, heightMm: 200 },
    LETTER: { widthMm: 215.9, heightMm: 279.4 },
    PORTRAIT_8X10IN: { widthMm: 203.2, heightMm: 254 },
    SQUARE_8_5IN: { widthMm: 215.9, heightMm: 215.9 },
};
export const PageOrientationSchema = z.enum(['portrait', 'landscape']);
export const BindingSchema = z.enum(['spread', 'single']);
export const SpreadLayoutIdSchema = z.enum(['split', 'full-bleed', 'band']);
export const PageTurnSchema = z.enum(['flip', 'slide', 'fade']);
export const CoverTypeSchema = z.enum(['soft', 'hard']);
export const OPTIONAL_BOOK_STEPS = ['things', 'locations'];
export const OptionalBookStepSchema = z.enum(OPTIONAL_BOOK_STEPS);
/**
 * Ширина разворота для стандартных форматов в **портретной** ориентации.
 *
 * Нужна из-за округления в ряду ISO: A5 — 148×210, но разворот из двух A5 это A4,
 * то есть 297×210, а не 296×210. Именно 297 мм фигурируют в 01-SPEC.md §3.10.3
 * и docs/04-PROMPT-FIXTURES.md §7. Для остальных случаев ширина разворота — 2× ширины
 * страницы (в альбомной ориентации удвоение попадает в ряд ISO точно).
 */
const ISO_SPREAD_WIDTH_MM = {
    A5: 297,
    A4: 420,
};
export const FormatAttributesSchema = z
    .object({
    /*
     * The format every book had before print sizes became brand data. A fallback for reading
     * stored attributes, not a choice for new books: those get their brand's first
     * `printSizes` entry through `withPageFormat`.
     */
    page: PageFormatSchema.default('A5'),
    orientation: PageOrientationSchema.default('portrait'),
    binding: BindingSchema.default('spread'),
    widthMm: z.number().positive().default(148),
    heightMm: z.number().positive().default(210),
    /** Вылеты под типографию, мм. */
    bleedMm: z.number().min(0).default(3),
    dpi: z.number().int().positive().default(300),
})
    .passthrough()
    .default({});
export const LayoutAttributesSchema = z
    .object({
    default: SpreadLayoutIdSchema.default('full-bleed'),
    /** Доля высоты разворота под картинку в раскладке `band`. */
    bandImageRatio: z.number().gt(0).lt(1).default(0.7),
    /** Для `split` — чередовать сторону текстовой страницы по разворотам. */
    alternateSides: z.boolean().default(true),
    allowPerSceneOverride: z.boolean().default(true),
})
    .passthrough()
    .default({});
export const NarrationReadingAttributesSchema = z
    .object({
    enabled: z.boolean().default(false),
    autoplay: z.boolean().default(true),
    volume: z.number().min(0).max(1).default(1),
})
    .passthrough()
    .default({});
export const ReadingAttributesSchema = z
    .object({
    fullscreen: z.boolean().default(true),
    pageTurn: PageTurnSchema.default('flip'),
    showFoldLine: z.boolean().default(false),
    autoplayVideo: z.boolean().default(true),
    narration: NarrationReadingAttributesSchema,
})
    .passthrough()
    .default({});
export const TypographyAttributesSchema = z
    .object({
    fontFamily: z.string().min(1).default('Playpen Sans'),
    baseSizePt: z.number().positive().default(14),
    /** Коридор автоподбора кегля `[min, max]`, 01-SPEC.md §3.10.6. */
    autoFitPt: z.tuple([z.number().positive(), z.number().positive()]).default([12, 14]),
    /** «Плашка» под строкой — `LineHighlightText` в `book-render`. */
    highlight: z.string().min(1).default('rgba(255,255,255,0.9)'),
    defaultTextColor: z.string().min(1).default('#4A3D52'),
})
    .passthrough()
    .default({});
export const VideoAttributesSchema = z
    .object({
    enabled: z.boolean().default(true),
    durationSec: z.number().positive().default(5),
    /** WAN 3.0: 720p — стандарт, 1080p — повышенное качество тарифа «Подарок». */
    resolution: z.enum(['720p', '1080p']).default('720p'),
    model: modelIdField(WAN_3_0_IMAGE_TO_VIDEO_MODEL_ID),
})
    .passthrough()
    .default({});
export const PrintAttributesSchema = z
    .object({
    coverType: CoverTypeSchema.default('soft'),
    colorProfile: z.string().min(1).default('sRGB'),
})
    .passthrough()
    .default({});
/**
 * Модели генерации. Размер картинки здесь **не задаётся** — он выводится
 * из раскладки разворота (`imageSpecFor`, 01-SPEC.md §3.10.3).
 */
export const GenerationAttributesSchema = z
    .object({
    imageModel: modelIdField(SEEDREAM_V5_PRO_EDIT_MODEL_ID),
    coverModel: modelIdField(SEEDREAM_V5_PRO_EDIT_MODEL_ID),
    /**
     * Ступень разрешения для моделей, которые не принимают пиксели
     * (Seedream 5). `1k` — как в веб-форме провайдера; для печати нужен `2k`.
     */
    imageResolution: z.enum(IMAGE_RESOLUTION_TIERS).default('1k'),
    variantsPerRequest: z.number().int().positive().default(1),
})
    .passthrough()
    .default({});
/** Состояние прохождения конструктора, которое должно переживать перезагрузку страницы. */
export const WorkflowAttributesSchema = z
    .object({
    skippedSteps: z.array(OptionalBookStepSchema).max(OPTIONAL_BOOK_STEPS.length).default([]),
})
    .passthrough()
    .default({});
export const BookAttributesV1Schema = z
    .object({
    schemaVersion: z.literal(BOOK_SCHEMA_VERSION).default(BOOK_SCHEMA_VERSION),
    format: FormatAttributesSchema,
    layout: LayoutAttributesSchema,
    reading: ReadingAttributesSchema,
    typography: TypographyAttributesSchema,
    video: VideoAttributesSchema,
    print: PrintAttributesSchema,
    generation: GenerationAttributesSchema,
    workflow: WorkflowAttributesSchema,
})
    .passthrough()
    .default({});
/** Полный набор атрибутов по умолчанию (01-SPEC.md §3.8). */
export function defaultBookAttributes() {
    return BookAttributesV1Schema.parse({});
}
/** Разбор атрибутов книги. Бросает `ZodError` на невалидных данных. */
export function parseBookAttributes(raw) {
    return BookAttributesV1Schema.parse(raw ?? {});
}
/** Attributes with the book switched to a standard print size, portrait. */
export function withPageFormat(attributes, page) {
    return {
        ...attributes,
        format: { ...attributes.format, page, orientation: 'portrait', ...PAGE_FORMAT_SIZES_MM[page] },
    };
}
/** Размер одной страницы книги в миллиметрах. */
export function pageSizeFor(attributes) {
    const { widthMm, heightMm } = attributes.format;
    return { widthMm: roundTo(widthMm, 3), heightMm: roundTo(heightMm, 3) };
}
/**
 * Размер разворота (двух страниц) в миллиметрах.
 * Для A5/A4 в портрете используется ширина из ряда ISO — см. `ISO_SPREAD_WIDTH_MM`.
 */
export function spreadSizeFor(attributes) {
    const page = pageSizeFor(attributes);
    const iso = ISO_SPREAD_WIDTH_MM[attributes.format.page];
    const widthMm = attributes.format.orientation === 'portrait' && iso !== undefined ? iso : page.widthMm * 2;
    return { widthMm: roundTo(widthMm, 3), heightMm: page.heightMm };
}
/** Размер страницы в пикселях при dpi книги. */
export function pageSizePx(attributes) {
    const { widthMm, heightMm } = pageSizeFor(attributes);
    const { dpi } = attributes.format;
    return { widthPx: mmToPx(widthMm, dpi), heightPx: mmToPx(heightMm, dpi) };
}
//# sourceMappingURL=attributes.js.map